// eslint-disable-next-line @typescript-eslint/no-var-requires
/* eslint-disable @typescript-eslint/camelcase */
import { Mutation, Action } from 'vuex-module-decorators';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { EdgeTokenApi } from '@trialcard/apigateway.client';
import VueRouter from 'vue-router';
import { Authentication } from 'auth0-js';
import { Resolve } from 'vue-di';
import { IAuthenticationData } from '@trialcard/apigateway.models';
import { RedirectLoginOptions, IdToken, PopupLoginOptions, getIdTokenClaimsOptions, GetTokenSilentlyOptions, GetTokenWithPopupOptions } from '@auth0/auth0-spa-js';
import { OnClear } from '~/services/clearModule';
import { Auth0Client } from '~/services/authentication';

// TODO: THIS IS A TEMPORARY CLASS
// THIS CLASS MUST BE UPDATED TO GO TO UAT OR BEYOND
@InjectModule({ stateFactory: true }, module)
@OnClear<SessionModule>(async value => value.logout())
export default class SessionModule extends InjectVuexModule {
	@Resolve
	public authentication!: Authentication;
	@Resolve
	edgeTokenApi!: EdgeTokenApi;


	type: 'guest' | 'user' | null = null;
	features: string[] = [];
	sessionId: number | null = null;
	isMiniVariant = false;

	public pending = false;
	public isAuthenticated = false;
	public popupOpen = false;
	public error = '';

	public email = '';
	public email_verified = false;
	public name = '';
	public nickname = '';
	public picture = '';
	public sub = '';
	public updated_at = '';

	public aud = '';
	public exp: number | null = null;
	public iat: number | null = null;
	public iss = '';
	public token = '';
	public profile: Auth0UserProfile | null = null;
	public 'https://apigateway.trialcard.com/portalId' = '';
	public 'https://apigateway.trialcard.com/roles': string[] = [];
	public 'https://apigateway.trialcard.com/account': number | null = null;
	public __raw = '';
	public nonce = '';
	public given_name = '';
	public family_name = '';

	public auth0Client() {
		return this.container.get<Auth0Client>(Auth0Client);
	}

	public get isHub() {
		return this['https://apigateway.trialcard.com/roles'] &&
			this['https://apigateway.trialcard.com/roles'].find(x => !!x && x.toLocaleLowerCase() === 'hub');
	}

	@Resolve
	public router!: VueRouter;

	public get isAuthenticatedUser() {
		return this.isAuthenticated;
	}

	@Action({ rawError: true })
	public async ensure() {
		try {
			// If the user is returning to the app after authentication..
			if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
				// handle the redirect and retrieve tokens
				const { appState } = await this.auth0Client().handleRedirectCallback();
				// Notify subscribers that the redirect callback has happened, passing the appState
				// (useful for retrieving any pre-authentication state)
				window.history.replaceState({}, document.title, window.location.pathname);
				// this.router.push(appState && appState.targetUrl ? appState.targetUrl : window.location.pathname);
			}
		} catch (error) {
			this.setError(error);
		} finally {
			// Initialize our internal authentication state
			try {
				await this.updateData();
				this.setPending(false);
			} catch (error) {
				this.setError('Failed to load user data. Reload this page to login again.');
				throw (this.error);
			}
		}
	}

	@Action({})
	public async updateData() {
		if (this.isAuthenticated) {
			const tokenUpdateRequired = !this.token || this["https://apigateway.trialcard.com/portalId"] !== process.env.PORTALID!
			if (tokenUpdateRequired) await this.updateToken();
			const isTokenExpired = !!this.exp && (Math.round(Date.now() / 1000) > Math.round(this.exp!));
			if (this.token && !this.profile) {
				const profile = await new Promise<Auth0UserProfile>((resolve, reject) => {
					this.authentication.userInfo(this.token, (e, profile) => {
						if (e) {
							reject(e);
						} else {
							resolve(profile);
						}
					});
				});
				this.setProfile(profile);
			}
			if (this.token && isTokenExpired) {
				await this.updateToken();
				this.setData({
					isAuthenticated: await this.auth0Client().isAuthenticated(),
					user: await this.auth0Client().getUser(),
					claims: await this.auth0Client().getIdTokenClaims(),
				});

			}

		} else {
			this.setData({
				isAuthenticated: await this.auth0Client().isAuthenticated(),
				user: await this.auth0Client().getUser(),
				claims: await this.auth0Client().getIdTokenClaims(),
			});
		}
	}

	@Action({})
	public async updateToken() {
		let token = '';
		try {
			token = await this.auth0Client().getTokenSilently({ scope: 'openId' });
		} catch (error) {
			if (error && error.error && error.error === 'login_required') {
				this.clearData();
				await this.auth0Client().loginWithRedirect({ scope: 'openId' });
			}
		} finally {
			this.setToken(token);
		}
	}

	@Action({})
	public async guestAuthentication() {
		const response = await this.edgeTokenApi.authenticationGuest({
			key: process.env.PORTALID!,
		});
		this.setGuestData(response.data.data!);
	}

	@Mutation
	public setGuestData(data: IAuthenticationData) {
		this.type = 'guest';
		this.token = data.token!;
		this.sessionId = data.sessionId || null;
		this.features = data.features || [];
	}

	@Mutation
	public setData({ isAuthenticated, user, claims }: { isAuthenticated: boolean; user: any; claims: IdToken }) {
		this.isAuthenticated = isAuthenticated;
		Object.assign(this, user);
		Object.assign(this, claims);
	}

	@Mutation
	public setToken(token: string) {
		this.token = token;
	}

	@Mutation
	public setError(error: string) {
		this.error = error;
		this.isAuthenticated = false;
	}

	@Action({})
	public async login() {
		this.loginWithRedirect();
	}

	@Mutation
	public clearData() {
		this.type = null;
		this.features = [];
		this.sessionId = null;
		this.isMiniVariant = false;
		this.pending = false;
		this.isAuthenticated = false;
		this.popupOpen = false;
		this.error = '';
		this.email = '';
		this.email_verified = false;
		this.name = '';
		this.nickname = '';
		this.picture = '';
		this.sub = '';
		this.updated_at = '';
		this.aud = '';
		this.exp = null;
		this.iat = null;
		this.iss = '';
		this.token = '';
		this.profile = null;
		this['https://apigateway.trialcard.com/portalId'] = '';
		this['https://apigateway.trialcard.com/account'] = null;
		this.__raw = '';
		this.nonce = '';
		this.given_name = '';
		this.family_name = '';
	}

	@Mutation
	public setPending(value: boolean) {
		this.pending = value;
	}

	@Mutation
	public setProfile(profile: Auth0UserProfile) {
		this.profile = profile;
	}

	@Mutation
	toggleMiniVariant() {
		this.isMiniVariant = !this.isMiniVariant;
	}

	@Action({})
	public async loginWithPopup(o?: PopupLoginOptions) {
		this.popupOpen = true;
		try {
			await this.auth0Client().loginWithPopup(o);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
		} finally {
			this.popupOpen = false;
		}
		await this.updateData();
	}
	@Action({})
	/** Handles the callback when logging in using a redirect */
	async handleRedirectCallback() {
		this.pending = true;
		try {
			await this.auth0Client().handleRedirectCallback();
			await this.updateData();
		} catch (error) {
			this.error = error;
		} finally {
			this.pending = false;
		}
	}
	@Action({})
	/** Authenticates the user using the redirect method */
	public loginWithRedirect(o?: RedirectLoginOptions) {
		return this.auth0Client().loginWithRedirect(o);
	}
	@Action({})
	/** Returns all the claims present in the ID token */
	public getIdTokenClaims(o?: getIdTokenClaimsOptions) {
		return this.auth0Client().getIdTokenClaims(o);
	}
	@Action({})
	/** Returns the access token. If the token is invalid or missing, a new one is retrieved */
	public getTokenSilently(o?: GetTokenSilentlyOptions) {
		return this.auth0Client().getTokenSilently(o);
	}
	@Action({})
	/** Gets the access token using a popup window */
	public getTokenWithPopup(o?: GetTokenWithPopupOptions) {
		return this.auth0Client().getTokenWithPopup(o);
	}

	/** Logs the user out and removes their session on the authorization server */
	@Action({})
	public logout(o?: any) {
        this.clearData();
		return this.auth0Client().logout(o);
	}
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface Auth0UserProfile {
	name: string;
	nickname: string;
	picture: string;
	user_id: string;
	username?: string;
	given_name?: string;
	family_name?: string;
	email?: string;
	email_verified?: boolean;
	clientID: string;
	gender?: string;
	locale?: string;
	identities: Auth0Identity[];
	created_at: string;
	updated_at: string;
	sub: string;
	user_metadata?: any;
	app_metadata?: any;
}

export interface Auth0Identity {
	connection: string;
	isSocial: boolean;
	provider: string;
	user_id: string;
}
/* eslint-enable @typescript-eslint/no-explicit-any */
